/**
 * Pricing Context
 * 동적 가격 정보를 전역적으로 관리하는 Context
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminAPI } from '../services/api';

interface ServicePrices {
  today: number;
  saju: number;
  tojung: number;
  compatibility: number;
  wealth: number;
  love: number;
}

interface SubscriptionPrices {
  '1day': number;
  '7days': number;
  '30days': number;
}

interface PricingResponse {
  success: boolean;
  data?: {
    servicePrices: ServicePrices;
    subscriptionPrices: SubscriptionPrices;
  };
}

interface PricingContextType {
  servicePrices: ServicePrices;
  subscriptionPrices: SubscriptionPrices;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// 기본값 (하드코딩된 fallback)
const defaultServicePrices: ServicePrices = {
  today: 3900,
  saju: 9900,
  tojung: 7900,
  compatibility: 12900,
  wealth: 5900,
  love: 6900
};

const defaultSubscriptionPrices: SubscriptionPrices = {
  '1day': 9900,
  '7days': 39000,
  '30days': 99000
};

const PricingContext = createContext<PricingContextType>({
  servicePrices: defaultServicePrices,
  subscriptionPrices: defaultSubscriptionPrices,
  loading: false,
  error: null,
  refetch: async () => {}
});

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return context;
};

interface PricingProviderProps {
  children: ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [servicePrices, setServicePrices] = useState<ServicePrices>(defaultServicePrices);
  const [subscriptionPrices, setSubscriptionPrices] = useState<SubscriptionPrices>(defaultSubscriptionPrices);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPricing = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminAPI.getPublicPricing() as PricingResponse;

      console.log('[PricingContext] API Response:', response);

      if (response.success && response.data) {
        console.log('[PricingContext] Setting prices:', {
          servicePrices: response.data.servicePrices,
          subscriptionPrices: response.data.subscriptionPrices
        });
        setServicePrices(response.data.servicePrices || defaultServicePrices);
        setSubscriptionPrices(response.data.subscriptionPrices || defaultSubscriptionPrices);
      } else {
        console.warn('[PricingContext] No data in response, using defaults');
      }
    } catch (err: any) {
      console.error('[PricingContext] Failed to load pricing:', err);
      setError(err.message || 'Failed to load pricing');
      // 에러 발생 시에도 기본값 사용 (앱 동작 유지)
      setServicePrices(defaultServicePrices);
      setSubscriptionPrices(defaultSubscriptionPrices);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const value: PricingContextType = {
    servicePrices,
    subscriptionPrices,
    loading,
    error,
    refetch: fetchPricing
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};
